import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FactureList, Facture } from "../../components/factures/FactureList";
import { FactureViewerModal } from "../../components/factures/FactureViewerModal";
import { FactureService } from "../../services/factures/factureService";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";

export const FournisseurFactures: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [factures, setFactures] = useState<Facture[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewedFacture, setViewedFacture] = useState<Facture | null>(null);
  const { id: factureIdFromUrl } = useParams<{ id: string }>();

  const fetchFactures = async () => {
    try {
      setLoading(true);
      const response = await FactureService.getFactures();
      setFactures(response.data.results);
    } catch (error) {
      console.error("Erreur chargement factures", error);
      toast.error(t('factures.management.errorValidation'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFactures();
  }, []);

  const handleFactureClick = (facture: Facture) => {
    if (!facture.fichier_original) {
      toast.error(t('factures.management.noFile'));
      return;
    }
    setViewedFacture(facture);
  };

  const handleCloseViewer = () => {
    setViewedFacture(null);
    if (factureIdFromUrl) navigate("/fournisseur/factures", { replace: true });
  };

  // Lien direct /fournisseur/factures/:id (ex. depuis une notification) : ouvre la facture
  useEffect(() => {
    if (!factureIdFromUrl || loading) return;
    const found = factures.find((f) => String(f.id) === factureIdFromUrl);
    if (found) {
      handleFactureClick(found);
      return;
    }
    FactureService.getFacture(factureIdFromUrl)
      .then((res) => handleFactureClick(res.data))
      .catch(() => {
        toast.error(t('factures.management.notFound'));
        navigate("/fournisseur/factures", { replace: true });
      });
  }, [factureIdFromUrl, loading]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('factures.fournisseurPages.manageTitle')}</h1>
          <p className="mt-1 text-sm text-gray-600">
            {t('factures.fournisseurPages.manageOverview')}
          </p>
        </div>
        <button 
          onClick={() => navigate('/fournisseur/factures/new')}
          className="mt-4 sm:mt-0 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none"
        >
            {t('factures.fournisseurPages.submitButton')}
        </button>
      </div>

      <FactureList 
        factures={factures} 
        onFactureClick={handleFactureClick} 
        isLoading={loading}
      />

      {viewedFacture && (
        <FactureViewerModal
          isOpen
          onClose={handleCloseViewer}
          factureUrl={viewedFacture.fichier_original || ""}
          factureNumero={viewedFacture.numero}
        />
      )}
    </div>
  );
};
